<script>
    import { onMount } from "svelte";

    import { user } from "../../stores";

    import {
        PageHead,
        PageLoading,
        RecordPaymentModal,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getValidBillingData,
        goBack,
        goToDownloadPage,
        handleDownloadPDF,
        isObjectEmpty,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import {
        getInsuranceFundById,
        getTrafficAccidentAuthorityById,
        getWorkerCompensationById,
    } from "../../Utils/utilCache.svelte";

    import ViewEstimatesHeadDetails from "./components/ViewEstimatesHeadDetails.svelte";
    import ViewEstimatesPatientDetails from "./components/ViewEstimatesPatientDetails.svelte";
    import ViewEstimatesProcedureDetails from "./components/ViewEstimatesProcedureDetails.svelte";
    import ViewEstimatesDoctorDetails from "./components/ViewEstimatesDoctorDetails.svelte";
    import ViewEstimatesEstimateDetails from "./components/ViewEstimatesEstimateDetails.svelte";
    import ViewEstimatesBillingDetails from "./components/ViewEstimatesBillingDetails.svelte";
    import ViewEstimatesPaymentTable from "./components/ViewEstimatesPaymentTable.svelte";

    import { headerRightBtnCls } from "../../Utils/utilConstants.svelte";

    export let params = {};

    let isLoading = false,
        isDownloadingPDF = false,
        isCancelEstimateModelOpen = false,
        isRecordPaymentModalOpen = false,
        isCancellingEstimate = false,
        estimateDetails = {},
        backupEstimateDetails = {},
        procedureDetails = {},
        doctorDetails = {},
        patientDetails = {},
        patientEncounterDetails = {},
        paymentDetails = {};

    let isEditEnabled = false,
        isUpdatingEstimate = false;

    let isEstimateEnableUncancel = false;

    let estimatePayments = [];

    $: if (
        params.id &&
        procedureDetails.patientEncounterId &&
        doctorDetails.id &&
        patientDetails.id &&
        patientEncounterDetails.id
    )
        isLoading = false;

    $: isUserAdmin = $user.role == 100;

    $: isUserDoctor = $user.role == 2;

    $: isUserEnableSeePaymentReceive = $user.role >= 20;

    $: isUserEnableCancelEstimate =
        isUserAdmin || $user.role == 20 || $user.role == 2;

    // Only allow estimate deletion if it has no payments or if all payments are either Cancelled (9) or Refunded (4, 5)
    $: isEstimateDeletable =
        estimateDetails.status != 9 &&
        (!estimateDetails.payments?.length ||
            estimateDetails.payments.every((payment) =>
                [9].includes(payment.status),
            ));

    $: isDeleteEstimateBtnDisabled = isCancellingEstimate;

    onMount(() => {
        if (params.id) getEstimateDetails(params.id);
    });

    const reload = () => {
        if (params.id) getEstimateDetails(params.id);
    };

    const reloadPayments = async () => {
        await apiMethods.getMethod.getEstimateById(params.id).then((res) => {
            estimatePayments = [];
            estimateDetails = { ...res };
            _updatePaymentDetails();
            backupEstimateDetails = { ...estimateDetails };
        });
        estimatePayments = estimateDetails.payments;
    };

    const getEstimateDetails = (id) => {
        isLoading = true;

        apiMethods.getMethod.getEstimateById(id).then((res) => {
            if (res.id) {
                estimateDetails = { ...res };

                _updateBillingItemsData();

                _updatePaymentDetails();

                loadPatientEncounterDetails(res.patientEncounterId);

                backupEstimateDetails = { ...estimateDetails };

                estimatePayments = estimateDetails.payments;
            }
        });
    };

    const loadPatientEncounterDetails = (id) => {
        apiMethods.getMethod
            .getPatientEncounterById(id, true)
            .then(async (res) => {
                if (res.id) {
                    patientEncounterDetails = { ...res };

                    await _updatePatientDetails();

                    _updateProcedureDetails();

                    _updateDoctorDetails();

                    isEstimateEnableUncancel = _checkEstimateEnableUncancel();
                } else
                    showError(
                        "Unable to load patient encounter details",
                        goBack,
                    );
            });
    };

    const getFile = () => {
        goToDownloadPage(0, estimateDetails.id);
    };

    const cancelEstimate = async () => {
        isCancellingEstimate = true;

        if (isEstimateDeletable)
            await apiMethods.putMethod
                .updateEstimateStatus(estimateDetails.id, 9)
                .then(async (res) => {
                    if (res.error) showError(res.error);
                    else {
                        estimateDetails = { ...res };
                        backupEstimateDetails = { ...res };

                        if (patientEncounterDetails.billingStatus == 1) {
                            await apiMethods.putMethod
                                .updatePatientEncounter({
                                    ...patientEncounterDetails,
                                    billingStatus: 0,
                                }, false, "Update status after cancel estimate")
                                .then((res) => {
                                    if (res.error) showError(res.error);
                                })
                                .catch((err) => showException(err));
                        }
                    }
                })
                .catch((err) => showException(err));
        else if (isEstimateEnableUncancel)
            await apiMethods.putMethod
                .updateEstimateStatus(estimateDetails.id, 1)
                .then((res) => {
                    if (res.error) showError(res.error);
                    else {
                        estimateDetails = { ...res };
                        backupEstimateDetails = { ...res };
                    }
                })
                .catch((err) => showException(err));

        isEstimateEnableUncancel = _checkEstimateEnableUncancel();

        isCancellingEstimate = false;

        closeCancelEstimateModel();
    };

    const openCancelEstimateModel = () => {
        isCancelEstimateModelOpen = true;
    };

    const closeCancelEstimateModel = () => {
        isCancelEstimateModelOpen = false;
    };

    const openRecordPaymentModal = (paymentData = {}) => {
        isRecordPaymentModalOpen = true;

        if (!isObjectEmpty(paymentData)) {
            paymentDetails = {
                ...paymentData,
                isEditPayment: true,
                billingDetails: backupEstimateDetails.billingDetails,
            };
        }
    };

    const closeRecordPaymentModal = (needReload, data) => {
        isRecordPaymentModalOpen = false;

        paymentDetails = { ...data };
    };

    const saveEstimate = async () => {
        isUpdatingEstimate = true;

        await apiMethods.putMethod
            .updateEstimate(estimateDetails)
            .then((res) => {
                if (res.id) reload();
                else {
                    showError(res.error);
                    cancelEditEstimate();
                }
            })
            .catch((err) => {
                showException(err);
                cancelEditEstimate();
            });

        isUpdatingEstimate = false;
        isEditEnabled = false;
    };

    const cancelEditEstimate = () => {
        isEditEnabled = false;
        estimateDetails = { ...backupEstimateDetails };
    };

    const _updateBillingItemsData = () => {
        if (estimateDetails.billingItems.length) {
            const lastItem = {
                isLast: true,
                totalAmount: estimateDetails.billingItems.reduce(
                    (sum, { totalAmount }) => sum + totalAmount,
                    0,
                ),
            };

            estimateDetails.billingItems.push(lastItem);
        }
    };

    const _updatePaymentDetails = () => {
        paymentDetails.relId = estimateDetails.id;
        paymentDetails.doctorId = estimateDetails.doctorId;
        paymentDetails.patientId = estimateDetails.patientId;
        paymentDetails.patientEncounterId = estimateDetails.patientEncounterId;
        paymentDetails.expireDate = estimateDetails.expireDate;
        paymentDetails.billingDetails = estimateDetails.billingDetails;
    };

    const _updateProcedureDetails = () => {
        const {
            procedureName,
            scheduledDate,
            dischargeDate,
            lengthInHours,
            createdDate,
            updatedDate,
            neuraxialType,
            typeOfBill,
            quoteOnly
        } = patientEncounterDetails;

        procedureDetails = {
            procedureName,
            scheduledDate,
            dischargeDate,
            lengthInHours,
            createdDate,
            updatedDate,
            neuraxialType,
            typeOfBill,
            quoteOnly,
            patientEncounterId: patientEncounterDetails.id,
        };

        if (patientEncounterDetails.hospital?.id) {
            procedureDetails = {
                ...procedureDetails,
                hospital: { ...patientEncounterDetails.hospital },
            };
        } else procedureDetails.hospital = {};

        if (patientEncounterDetails.surgeon?.id) {
            procedureDetails = {
                ...procedureDetails,
                surgeon: { ...patientEncounterDetails.surgeon },
            };
        } else procedureDetails.surgeon = {};
    };

    const _updatePatientDetails = async () => {
        const {
            opvStatusMedicare,
            opvStatusHealthFund,
            opvManuallyApproved,
            opvStatusVeteran,
        } = patientEncounterDetails;

        patientDetails = {
            ...patientEncounterDetails.patient,
            opvStatusMedicare,
            opvStatusHealthFund,
            opvManuallyApproved,
            opvStatusVeteran,
            patientEncounterId: patientEncounterDetails.id,
        };

        if (patientEncounterDetails.billingData.length) {
            const lastBillingData = getValidBillingData(
                patientEncounterDetails.billingData,
            );

            const {
                patientInsurance,
                patientInsuranceType,
                patientInsuranceId,
            } = lastBillingData;

            patientDetails.patientInsurance = patientInsurance;

            if (patientInsuranceId) {
                let res =
                    patientInsuranceType == 0
                        ? await getInsuranceFundById(patientInsuranceId)
                        : patientInsuranceType == 16
                          ? await getWorkerCompensationById(patientInsuranceId)
                          : patientInsuranceType == 17
                            ? await getTrafficAccidentAuthorityById(
                                  patientInsuranceId,
                              )
                            : {};

                patientDetails.insuranceName = res?.name ?? "";
            } else patientDetails.insuranceName = "";
        } else
            patientDetails = {
                ...patientDetails,
                insuranceName: "",
                patientInsurance: "",
            };
    };

    const _updateDoctorDetails = () => {
        const {
            doctor: {
                firstName,
                lastName,
                fullName,
                title,
                phone,
                id,
                profile,
            },
            doctorProvider: {
                number,
                location,
                locationAddress,
                locationAddress2,
                city,
                state,
                postCode,
                isNoGapBupa,
                isNoGapHCF,
                isNoGapNib,
                isPreferredDefence,
            },
        } = patientEncounterDetails;

        doctorDetails = {
            firstName,
            lastName,
            fullName,
            title,
            phone,
            id,
            number,
            location,
            locationAddress,
            locationAddress2,
            city,
            state,
            postCode,
            isNoGapBupa,
            isNoGapHCF,
            isNoGapNib,
            isPreferredDefence,
            profile,
        };
    };

    const _checkEstimateEnableUncancel = () => {
        if (estimateDetails.status != 9) return false;

        const { estimates } = patientEncounterDetails;

        if (estimates.length == 1) return true;

        return !estimates
            .filter((estimate) => estimate.id != estimateDetails.id)
            .some((estimate) => estimate.status != 9);
    };
</script>

{#if isLoading}
    <PageLoading />
{:else}
    <PageHead title="View quote details">
        {#if isEstimateDeletable && isUserEnableCancelEstimate}
            <button
                class="btn-warning {headerRightBtnCls}
            "
                on:click={openCancelEstimateModel}
            >
                <i class="fa fa-times"></i>
                Cancel Quote
            </button>
        {:else if isEstimateEnableUncancel && isUserEnableCancelEstimate}
            <button
                class="btn-warning {headerRightBtnCls}
            "
                on:click={openCancelEstimateModel}
            >
                <i class="fa-solid fa-rotate-left"></i>
                Uncancel Quote
            </button>
        {/if}

        {#if isUserAdmin && backupEstimateDetails.status !== Number(9)}
            <button
                class={headerRightBtnCls}
                on:click={() => {
                    openRecordPaymentModal();
                }}
            >
                <i class="fa fa-credit-card"></i>
                Record Payment
            </button>
        {/if}

        <button
            class={`${headerRightBtnCls} ${isDownloadingPDF ? "loading" : ""}`}
            on:click={getFile}
            disabled={isDownloadingPDF}
        >
            <i class="fas fa-download" />
            {isDownloadingPDF ? "Downloading PDF" : "Download quote PDF"}
        </button>
    </PageHead>

    <div class="flex-1 relative mt-4">
        <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4 px-2">
            <ViewEstimatesHeadDetails estimateDetails={backupEstimateDetails} />

            <ViewEstimatesPatientDetails
                {patientDetails}
                {patientEncounterDetails}
                loadData={reload}
            />

            <ViewEstimatesProcedureDetails {procedureDetails} {doctorDetails} />

            <!-- {#if isUserAdmin}
                <ViewEstimatesDoctorDetails {doctorDetails} />
            {/if} -->

            <ViewEstimatesEstimateDetails
                {patientDetails}
                bind:estimateDetails
                bind:isEditEnabled
                bind:isUpdatingEstimate
                on:saveEstimate={saveEstimate}
                on:cancelEditEstimate={cancelEditEstimate}
            />

            <ViewEstimatesBillingDetails
                billingItems={backupEstimateDetails.billingItems}
                {patientEncounterDetails}
            />

            <ViewEstimatesPaymentTable
                {estimatePayments}
                {isUserDoctor}
                reload={reloadPayments}
                {isUserEnableSeePaymentReceive}
                {openRecordPaymentModal}
                {patientDetails}
            />
        </div>
    </div>
{/if}

<div
    class={isCancelEstimateModelOpen
        ? "modal modal-open sm:modal-middle"
        : "modal"}
>
    <div class="modal-box">
        <div class="p-4">
            <p class="leading-6 text-error">
                {isEstimateDeletable ? "Cancel" : "Uncancel"} this quote?
            </p>
        </div>
        <div class="modal-action flex items-center justify-between flex-1">
            <button
                class="btn btn-secondary text-white btn-sm border-none w-48"
                on:click={closeCancelEstimateModel}>Cancel</button
            >
            <button
                class={`btn btn-error text-white btn-sm border-none w-48 ${
                    isDeleteEstimateBtnDisabled ? "loading" : ""
                }`}
                on:click={cancelEstimate}
                disabled={isDeleteEstimateBtnDisabled}
            >
                {isDeleteEstimateBtnDisabled ? "Processing" : "Confirm"}
            </button>
        </div>
    </div>
</div>

{#if isRecordPaymentModalOpen}
    <RecordPaymentModal
        {isRecordPaymentModalOpen}
        {paymentDetails}
        route="estimate"
        {closeRecordPaymentModal}
        on:paymentRecorded={reloadPayments}
    />
{/if}
